import { Box, Button, Heading, Stack, Tag } from '@chakra-ui/react';
import { getTenantById, getTenantId } from '@pelicargo/tenants';
import { Show } from '@pelicargo/ui';
import { getContactOptions, modelUtils, prettyDate, prettyDateRelative } from '@pelicargo/utils';
import { CargoType, DocumentType, FreightService } from '@prisma/client';
import { useMemo } from 'react';
import { HiChevronDown, HiChevronUp } from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useAccountType } from '../../hooks/accounts/useAccountType';
import { useDocuments } from '../../hooks/documents/useDocuments';
import { useRequestForm } from '../../hooks/request/useForm';
import { useRelatedRequests } from '../../hooks/request/useRelated';
import { useUpdateRequest } from '../../hooks/request/useUpdate';
import { useSetWatchingRequest } from '../../hooks/requestWatcher/useSetCurrent';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { DocumentsForm } from '../Documents/DocumentsForm ';
import { LabeledText } from '../LabeledText';
import { RelatedRequests } from '../RelatedRequests';
import { SimpleForm } from '../SimpleForm';
import { SimpleLabeledPopoverInput } from '../SimpleLabeledPopoverInput';
import { SimplePopoverInput } from '../SimplePopoverInput';
import { SimpleReactSelect } from '../SimpleReactSelect';
import { SimpleTypesenseSelect } from '../SimpleTypesenseSelect';
import { CargoDetailsForm } from './CargoDetailsForm';
import { RequestHeader } from './RequestHeader';
import { TrackingShipments } from './TrackingShipments';

export const RequestDetail = () => {
  const { request } = useCurrentRequest();
  const { data } = trpc.getAllAccounts.useQuery(undefined, { refetchOnWindowFocus: false });
  const { mutate: updateRequest } = useUpdateRequest(request?.id);
  const { methods } = useRequestForm(request);
  const { data: contactsFromAccount } = trpc.getContactsByAccountId.useQuery({ id: request?.account_id });

  useSetWatchingRequest(request?.id);

  const type = request?.account?.type;
  const iata = request?.account?.iata_number;
  const accountType = useAccountType(type, iata);

  const creatorName = useMemo(
    () => [request?.author?.full_name]?.filter((n) => !!n)?.join(' '),
    [request?.author?.full_name],
  );

  const { related, sampleList, remaining, hasDuplicates, hasSimilar, disclosure, maxPreview } = useRelatedRequests(
    request?.id,
    5,
  );

  const contactOptions = getContactOptions(contactsFromAccount || []);

  const isFCA = useMemo(() => request?.freight_services?.includes(FreightService.FCA), [request?.freight_services]);
  const isEXW = useMemo(() => request?.freight_services?.includes(FreightService.EXW), [request?.freight_services]);

  const isDgd = useMemo(() => request?.cargo_type === CargoType.DangerousGoods, [request?.cargo_type]);

  const fulfillmentPartners = useMemo(
    () =>
      request?.quotes?.reduce((acc, quote) => {
        const partner = quote?.fulfillment_partner_configuration?.account_as_fulfillment_partner;
        if (partner && !acc.find((p) => p.id === partner.id) && quote?.freight_service === FreightService.AFR) {
          acc.push(partner);
        }
        return acc;
      }, []) ?? [],
    [request?.quotes],
  );
  const [documents] = useDocuments(request?.id);
  const documentTypes = useMemo(() => {
    const suggested: DocumentType[] = [];

    if (isFCA || isEXW) {
      suggested.push(DocumentType.SLI, DocumentType.SHIPPER_DOCUMENTS, DocumentType.AIRWAY_BILL, DocumentType.BOL);
    }
    if (isEXW) suggested.push(DocumentType.LABELS);
    if (isDgd) suggested.push(DocumentType.DGD, DocumentType.MSDS);

    // Remove the ones any FP has already uploaded
    const fpIds = fulfillmentPartners.map((partner) => partner.id);
    const fpUploadTypes = documents.filter((doc) => fpIds.includes(doc.account_id)).map((doc) => doc.type);

    return suggested.filter((type, index, self) => self.indexOf(type) === index && !fpUploadTypes.includes(type));
  }, [isFCA, isEXW, isDgd, fulfillmentPartners, documents]);

  const firstBookedAt = useMemo(() => {
    const bookedQuotes = request?.quotes?.filter((quote) => quote?.booked_at);
    return bookedQuotes?.sort((a, b) => new Date(a?.booked_at)?.getTime() - new Date(b?.booked_at)?.getTime())?.[0]
      ?.booked_at;
  }, [request?.quotes]);

  const isInland = modelUtils.request.requiresInland(request ?? { freight_services: [] });

  const noRequiredFP =
    modelUtils.request.requiresInland(request ?? { freight_services: [] }) && !request?.required_fulfillment_partner_id;

  return (
    <Box borderRightWidth={1} w="450px" overflowY="auto" h="100vh">
      <RequestHeader request={request} />
      <Show if={hasDuplicates || hasSimilar}>
        <Stack direction="column" spacing={1} borderBottomWidth={1} p={4}>
          {/* Two states: sample mode or not */}
          <Stack spacing={3}>
            <Show if={!disclosure.isOpen}>
              <RelatedRequests related={sampleList} limit={maxPreview} isDuplicate={hasDuplicates} />
            </Show>
            <Show if={disclosure.isOpen}>
              <Show if={hasDuplicates}>
                <RelatedRequests related={related?.duplicate} isDuplicate />
              </Show>
              <Show if={hasSimilar}>
                <RelatedRequests related={related?.similar} />
              </Show>
            </Show>
          </Stack>
          <Show if={remaining > 0}>
            <Button
              onClick={disclosure.onToggle}
              variant="ghost"
              alignSelf="center"
              size="xs"
              colorScheme="brand"
              rightIcon={disclosure.isOpen ? <HiChevronUp /> : <HiChevronDown />}
            >
              {disclosure.isOpen ? 'Minimize' : `Expand`}
            </Button>
          </Show>
        </Stack>
      </Show>
      <SimpleForm onValid={updateRequest} defaultValues={request} {...methods}>
        <Stack direction="column" spacing={2} borderBottomWidth={1} py={4} p={2}>
          <Heading fontSize="lg" ml="2" py={2}>
            Customer
          </Heading>
          <Stack direction="column" spacing={0}>
            <LabeledText label="Created by" value={creatorName} isHorizontal px={2} py={1} />
            <SimpleLabeledPopoverInput
              flex={1}
              name="account_id"
              label="Account"
              inputType="react-select"
              isHorizontal
              options={
                data?.map((account) => ({
                  value: account?.id,
                  label: account?.name,
                })) ?? []
              }
              isSparkling={new Date() < new Date(request?.account?.highlight_until)}
              tabIndex={0}
            />
            <LabeledText flex={1} label="Type" value={accountType.value} isHorizontal px={2} py={1} />
            <LabeledText
              flex={1}
              label="Flags"
              value={
                <Stack direction="row" spacing={1}>
                  <Show if={request?.account?.verified_at === null}>
                    <Tag size="sm" colorScheme="red">
                      Unverified Account
                    </Tag>
                  </Show>
                  {request.account.account_flags.map((flag) => (
                    <Tag size="sm" colorScheme="gray" key={flag.flag_name}>
                      {flag.flag_name}
                    </Tag>
                  ))}
                </Stack>
              }
              isHorizontal
              px={2}
              py={1}
            />
            <LabeledText
              flex={1}
              label="Tenant"
              value={getTenantById(request.tenant_id)?.name}
              isHighlighted={request.tenant_id !== getTenantId()}
              isHorizontal
              px={2}
              py={1}
            />
            <Show if={isInland}>
              <SimplePopoverInput
                name="required_fulfillment_partner_id"
                label="Fulfillment Partner"
                tabIndex={0}
                displayValue={request?.required_fulfillment_partner?.name || '-'}
                isHorizontal
                px={2}
                isHighlighted={noRequiredFP}
              >
                <SimpleTypesenseSelect
                  name="required_fulfillment_partner_id"
                  collection="accounts"
                  queryBy="name,account_id"
                  documentLabel="name"
                  documentValue="account_id"
                  shouldProcessValue
                  suffixWithValue
                  isMulti={false}
                  value={{
                    value: request?.required_fulfillment_partner?.id,
                    name_label: request?.required_fulfillment_partner?.name,
                    label: request?.required_fulfillment_partner?.name,
                  }}
                />
              </SimplePopoverInput>
            </Show>

            <SimplePopoverInput
              name="default_sender_contact_id"
              label="Primary Contact"
              tabIndex={0}
              displayValue={request?.default_sender_contact?.full_name || '-'}
              isHorizontal
              px={2}
            >
              <SimpleReactSelect name="default_sender_contact_id" options={contactOptions} />
            </SimplePopoverInput>
            <LabeledText
              label="Email"
              textProps={{ noOfLines: 1 }}
              value={request?.default_sender_contact?.email || '-'}
              isCopyable
              isHorizontal
              px={2}
              py={1}
            />
            <LabeledText
              label="Phone"
              textProps={{ noOfLines: 1 }}
              value={request?.default_sender_contact?.phone_number || '-'}
              isCopyable
              isHorizontal
              px={2}
              py={1}
            />
            <LabeledText
              label="Booked At"
              textProps={{ noOfLines: 1 }}
              value={prettyDateRelative(firstBookedAt, { showTime: true }) || '-'}
              title={prettyDate(firstBookedAt, { showTime: true }) || '-'}
              isHorizontal
              px={2}
              py={1}
            />
          </Stack>
        </Stack>
      </SimpleForm>
      <Stack direction="column" spacing={2} borderBottomWidth={1} py={4} p={2}>
        <Heading fontSize="lg" ml="2" py={2}>
          Cargo
        </Heading>
        <CargoDetailsForm request={request} />
      </Stack>
      <Stack direction="column" spacing={2} borderBottomWidth={1} py={4} p={2}>
        <Heading fontSize="lg" ml="2" py={2}>
          Documents
        </Heading>
        <Stack direction="column" spacing={4} px={2}>
          <DocumentsForm
            requestId={request?.id}
            accountId={request?.account_id}
            documentTypes={documentTypes}
            heading={request?.account?.name}
          />
          {fulfillmentPartners.map((partner) => (
            <DocumentsForm key={partner.id} heading={partner?.name} requestId={request?.id} accountId={partner?.id} />
          ))}
        </Stack>
      </Stack>
      <Stack direction="column" spacing={2} borderBottomWidth={1} py={4} p={2} position="relative">
        <Heading fontSize="lg" ml="2" py={2}>
          Tracking
        </Heading>
        <TrackingShipments />
      </Stack>
    </Box>
  );
};
